/**
 * The formatting bar's text colours. Saved as a name (<span data-colour="red">),
 * never a code: the website picks the shade that reads on its own background
 * (website index.css, and theme-matchday.css for the dark theme). The names
 * mirror TEXT_COLOURS in lambda/lib/rich-text.ts, and
 * lambda/lib/rich-text.mirror.test.ts checks every copy has all twelve.
 *
 * Inside the editor a colour is a <font color>, because that is what the
 * browser's own colour command makes, and so what it knows how to split,
 * replace and clear. toEditorColours and fromEditorColours swap between the
 * two forms on the way in and out.
 */
export const TEXT_COLOURS = [
  { key: 'red', label: 'Red', hex: '#c8102e' },
  { key: 'claret', label: 'Claret', hex: '#7c1d3f' },
  { key: 'orange', label: 'Orange', hex: '#c2410c' },
  { key: 'gold', label: 'Gold', hex: '#946200' },
  { key: 'green', label: 'Green', hex: '#137638' },
  { key: 'teal', label: 'Teal', hex: '#0f766e' },
  { key: 'sky', label: 'Sky blue', hex: '#0369a1' },
  { key: 'blue', label: 'Blue', hex: '#1d4ed8' },
  { key: 'navy', label: 'Navy', hex: '#1e3a8a' },
  { key: 'purple', label: 'Purple', hex: '#7e22ce' },
  { key: 'pink', label: 'Pink', hex: '#be185d' },
  { key: 'grey', label: 'Grey', hex: '#4b5563' },
];

const KEY_BY_HEX = new Map(TEXT_COLOURS.map(c => [c.hex, c.key]));
const HEX_BY_KEY = new Map(TEXT_COLOURS.map(c => [c.key, c.hex]));

/** '#C8102E', 'c8102e' and 'rgb(200, 16, 46)' all come back as '#c8102e'. */
export function normaliseColour(value: string | null | undefined): string {
  const v = (value || '').trim().toLowerCase();
  const rgb = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return '#' + rgb.slice(1, 4).map(n => Number(n).toString(16).padStart(2, '0')).join('');
  if (/^#?[0-9a-f]{6}$/.test(v)) return v.startsWith('#') ? v : `#${v}`;
  if (/^#?[0-9a-f]{3}$/.test(v)) return '#' + v.replace('#', '').split('').map(c => c + c).join('');
  return v;
}

const unwrap = (el: Element) => el.replaceWith(...Array.from(el.childNodes));

function rewrap(el: Element, tag: string, attr: string, value: string) {
  const next = el.ownerDocument.createElement(tag);
  next.setAttribute(attr, value);
  next.append(...Array.from(el.childNodes));
  el.replaceWith(next);
}

/** Saved HTML to editor HTML: each colour span becomes a <font color>. */
export function toEditorColours(html: string): string {
  if (!html.includes('data-colour')) return html;
  const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
  body.querySelectorAll('span[data-colour]').forEach(span => {
    const hex = HEX_BY_KEY.get(span.getAttribute('data-colour') || '');
    if (hex) rewrap(span, 'font', 'color', hex);
    else unwrap(span);
  });
  return body.innerHTML;
}

/**
 * The editor's HTML as it is saved: a colour from the list becomes a colour
 * span, and any other colour or span the browser added is dropped, keeping the
 * words. "No colour" paints in the editor's own text colour, which is not on
 * the list, so it goes here too.
 */
export function fromEditorColours(el: HTMLElement): string {
  const root = el.cloneNode(true) as HTMLElement;
  root.querySelectorAll('font, span').forEach(node => {
    const named = node.getAttribute('data-colour');
    const key = named && HEX_BY_KEY.has(named)
      ? named
      : KEY_BY_HEX.get(normaliseColour(node.tagName === 'FONT' ? node.getAttribute('color') : (node as HTMLElement).style.color));
    if (key) rewrap(node, 'span', 'data-colour', key);
    else unwrap(node);
  });
  return root.innerHTML;
}
